import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useStore } from "../context/StoreContext";
import { t } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Trash2, ShoppingBag, Package } from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";

export default function Cart() {
  const { cart, lang, updateQty, removeFromCart, formatPrice } = useStore();
  const navigate = useNavigate();
  const [consolidate, setConsolidate] = React.useState(true);
  const subtotal = cart.reduce((s, i) => s + i.product.price_usd * i.qty, 0);

  if (cart.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-24 text-center">
        <ShoppingBag size={56} className="mx-auto mb-4 text-gray-300" />
        <h2 className="text-2xl font-semibold mb-4">{t(lang, "empty_cart")}</h2>
        <Link to="/products"><Button className="btn-primary-thiqa rounded-full px-8">{t(lang, "continue_shopping")}</Button></Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-8 py-10 md:py-14">
      <h1 className={`${lang === "ar" ? "font-arabic-heading" : "font-english-heading"} text-4xl font-semibold mb-8`} style={{ color: "var(--thiqa-primary)" }}>
        {t(lang, "cart")}
      </h1>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {cart.map((item) => {
            const name = lang === "ar" ? item.product.name_ar : item.product.name_en;
            return (
              <div key={item.id} className="bg-white border border-[#e8ddc9] rounded-2xl p-4 flex gap-4 items-center" data-testid={`cart-item-${item.id}`}>
                <img src={item.product.image} alt={name} className="w-20 h-20 md:w-24 md:h-24 object-cover rounded-xl" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm md:text-base line-clamp-2">{name}</h3>
                  <p className="text-sm text-gray-600 mt-1">{formatPrice(item.product.price_usd)}</p>
                </div>
                <div className="flex items-center border border-gray-300 rounded-full">
                  <button onClick={() => updateQty(item.id, item.qty - 1)} className="px-3 py-1.5">−</button>
                  <span className="px-3 text-sm">{item.qty}</span>
                  <button onClick={() => updateQty(item.id, item.qty + 1)} className="px-3 py-1.5">+</button>
                </div>
                <button data-testid={`remove-${item.id}`} onClick={() => removeFromCart(item.id)} className="text-red-500 hover:text-red-700 p-2">
                  <Trash2 size={18} />
                </button>
              </div>
            );
          })}
          <div className="bg-[#faf3e3] border border-[#e8ddc9] rounded-2xl p-4 flex items-start gap-3">
            <Checkbox data-testid="consolidate-toggle" checked={consolidate} onCheckedChange={setConsolidate} className="mt-1" />
            <div>
              <div className="flex items-center gap-2 font-semibold">
                <Package size={18} style={{ color: "var(--thiqa-primary)" }} />
                {t(lang, "consolidate")}
              </div>
              <p className="text-xs text-gray-600 mt-1">{t(lang, "consolidate_hint")}</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#e8ddc9] rounded-2xl p-6 h-fit sticky top-24" data-testid="cart-summary">
          <h3 className="font-semibold text-lg mb-4">{t(lang, "subtotal")}</h3>
          <div className="flex justify-between mb-2">
            <span className="text-gray-600 text-sm">{t(lang, "subtotal")}</span>
            <span className="font-medium">{formatPrice(subtotal)}</span>
          </div>
          <p className="text-xs text-gray-500 mb-4">
            {lang === "ar" ? "الجمارك والشحن تُحسب في صفحة الدفع بناءً على الدولة" : "Customs and shipping calculated at checkout based on country"}
          </p>
          <Button
            data-testid="proceed-checkout"
            className="w-full btn-primary-thiqa rounded-full py-6"
            onClick={() => navigate("/checkout", { state: { consolidate } })}
          >
            {t(lang, "checkout")}
          </Button>
        </div>
      </div>
    </div>
  );
}
